import ApplicationComponent from "legacy/ApplicationComponent"
import LoadingView from "legacy/LoadingView"
import Paginator from "legacy/Paginator"
import {
  afterSetState,
  badgeForPaymentStatus,
  buildRoute,
  centsToDollars,
  humanize,
  updateUrlQueryParams,
} from "legacy/helpers.es6"
import SaleEmptyView from "legacy/sales/SaleEmptyView"
import moment from "moment"
import PropTypes from "prop-types"
import React from "react"

// Note: jQuery ($) is loaded globally

class SaleAccountList extends ApplicationComponent {
  constructor(args) {
    super(args)
    this.state = {
      sales: this.props.items,
      page: this.props.page,
      numberOfPages: this.props.pageCount,
      totalSalesCount: this.props.totalCount,
      isLoading: false,
      errorMessage: null,
    }
    this.goToPage = this.goToPage.bind(this)
    this.updateUrl = this.updateUrl.bind(this)

    // Get currency from props with USD as default
    this.currency = this.props.currency || "USD"
  }

  fetchSales() {
    this.setState({ isLoading: true })
    $.ajax({
      url: `${$context.salesEndpoint}?page=${this.state.page}`,
      type: "GET",
      dataType: "json",
      contentType: "application/json",
      success: (data) => {
        this.setState({
          isLoading: false,
          sales: data.items,
          numberOfPages: data.pageCount,
          totalSalesCount: data.totalCount,
        })
        afterSetState(this.updateUrl)
      },
      error: (data) => {
        this.setState({
          isLoading: false,
          errorMessage: data.responseJSON.error,
        })
      },
    })
  }

  goToPage(page) {
    this.setState({ page })
    afterSetState(() => {
      this.fetchSales()
      this.updateUrl()
    })
  }

  updateUrl() {
    updateUrlQueryParams({ page: this.state.page })
  }

  scheduledPayment(sale) {
    return sale.status === "refunded"
      ? sale.scheduledPayments[sale.scheduledPayments.length - 1]
      : sale.scheduledPayments[0]
  }

  renderPaymentMethod(sale) {
    const scheduledPayment = this.scheduledPayment(sale)
    switch (scheduledPayment.method) {
      case "credit":
        return (
          <div className="flex items-center space-x-1">
            <img
              src={sale.card.logo}
              className="m-0 h-6 w-6"
              alt={`${sale.card.brand} credit card logo`}
            />{" "}
            <p className="m-0">{sale.card.last4}</p>
          </div>
        )
      case "check":
        return (
          <div className="flex space-x-1">{`${
            scheduledPayment.method && humanize(scheduledPayment.method)
          } ${sale.checkNumber || ""}`}</div>
        )
      default:
        return (
          <div className="flex space-x-1">
            {scheduledPayment.method && humanize(scheduledPayment.method)}
          </div>
        )
    }
  }

  renderSale(sale, index) {
    return (
      <tr
        className={`h-12 ${index % 2 !== 0 ? "bg-gray-100" : "bg-transparent"}`}
        key={sale.id}
      >
        <td className="pl-6">
          {moment(sale.createdAt).format("MMM D YYYY, HH:mm")}
        </td>
        <td>
          <span
            className={`h-6 p-1 font-semibold ${badgeForPaymentStatus(
              sale.status
            )}`}
          >
            {humanize(sale.status)}
          </span>
        </td>
        <td>{this.renderPaymentMethod(sale)}</td>
        <td className="w-96 font-semibold">
          {centsToDollars(
            sale.status === "refunded" ? sale.refundedAmount : sale.totalPrice,
            2,
            this.currency
          )}
        </td>
        <td className="w-20 font-semibold">{`#${sale.id}`}</td>
        <td className="w-20">
          {sale.status !== "failed" && (
            <a
              className="btn btn-secondary btn-sm"
              href={buildRoute($context.receiptUrl, { sale_id: sale.id })}
            >
              Receipt
            </a>
          )}
        </td>
        <td className="w-24">
          <a
            className="btn btn-secondary btn-sm"
            href={buildRoute($context.saleUrl, { sale_id: sale.id })}
          >
            View Details
          </a>
        </td>
      </tr>
    )
  }

  renderSales() {
    let index = 0 // just for alternating colors on the backgrounds of the rows
    return (
      <div>
        {$context.mbmSaleHistoryEnabled && (
          <h4 className="p-2 pt-6">Legacy Sales</h4>
        )}
        <div className="table-responsive">
          <table className="w-full">
            <thead>
              <tr>
                <th className="pl-6">Date</th>
                <th className="text-left">Status</th>
                <th className="text-left">Payment</th>
                <th className="text-left">Total</th>
                <th className="sr-only text-right">ID</th>
                <th className="sr-only text-right">Receipt</th>
                <th className="sr-only text-right">View Details</th>
              </tr>
            </thead>
            <tbody>
              {this.state.sales.map((sale) => {
                index += 1
                return this.renderSale(sale, index)
              })}
            </tbody>
          </table>
        </div>
        <hr />
        {this.state.numberOfPages > 1 && (
          <div className="flex">
            <Paginator
              page={this.state.page}
              numberOfPages={this.state.numberOfPages}
              totalItemsCount={this.state.totalSalesCount}
              goToPage={this.goToPage}
            />
          </div>
        )}
      </div>
    )
  }

  render() {
    if (this.state.isLoading) {
      return <LoadingView name="Sales" />
    } else if (this.state.errorMessage) {
      return <p className="italics">{this.state.errorMessage}</p>
    } else if (this.state.sales.length === 0) {
      if ($context.mbmSaleHistoryEnabled) return null
      return (
        <SaleEmptyView
          title={`${this.props.name} has no sales yet!`}
          description="Picture all your sales neatly lined up here (just like stacks of money)."
          createUrl={$context.createUrl}
          canCreateNewSales={this.props.canPerformNewActionsOnContact}
        />
      )
    } else {
      return this.renderSales()
    }
  }
}

SaleAccountList.propTypes = {
  items: PropTypes.array.isRequired,
  page: PropTypes.number,
  pageCount: PropTypes.number,
  totalCount: PropTypes.number,
  name: PropTypes.string.isRequired,
  canPerformNewActionsOnContact: PropTypes.bool,
  currency: PropTypes.string,
}

SaleAccountList.defaultProps = {
  items: [],
  page: 1,
  pageCount: 0,
  totalCount: 0,
  canPerformNewActionsOnContact: false,
}

export default SaleAccountList
